import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { AlertCircle, CheckCircle2, Link2 } from 'lucide-react'
import type { RelacionadorMetricas } from '../../services/supervisaoVendas.service'
import type { VinculoResolvido } from '../../services/vinculoResolver'

interface RelacionadorDetalhesModalProps {
  relacionador: RelacionadorMetricas
  periodo: {
    mes: number
    ano: number
  }
  vinculos: VinculoResolvido[]
  onClose: () => void
}

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

export function RelacionadorDetalhesModal({
  relacionador,
  periodo,
  vinculos,
  onClose,
}: RelacionadorDetalhesModalProps) {
  // Filtrar vínculos do relacionador
  const vinculosRelacionador = vinculos.filter(
    v => v.relacionadorId === relacionador.relacionadorId
  )

  const vinculadosExplicitos = vinculosRelacionador.filter(v => v.origem === 'EXPLICITO')
  const vinculadosAgendamento = vinculosRelacionador.filter(v => v.origem === 'AGENDAMENTO')
  const vinculadosPipeline = vinculosRelacionador.filter(v => v.origem === 'PIPELINE')
  const semVinculo = vinculosRelacionador.filter(v => v.origem === 'SEM_VINCULO')

  const temVisualizarVinculos = semVinculo.length > 0

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{relacionador.nome}</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="resumo" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="resumo">Resumo</TabsTrigger>
            <TabsTrigger value="vinculos">
              Vínculos
              {temVisualizarVinculos && (
                <Badge variant="destructive" className="ml-1.5 px-1">
                  {semVinculo.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="meta">Meta & Comissão</TabsTrigger>
          </TabsList>

          {/* Aba Resumo */}
          <TabsContent value="resumo" className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Reuniões</p>
                <p className="text-2xl font-bold">{relacionador.reunioesEste}</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Produção Total</p>
                <p className="text-2xl font-bold">{relacionador.produzidoTotal}</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Vinculados</p>
                <p className="text-2xl font-bold">{relacionador.produzidoVinculado}</p>
              </div>
              <div className="rounded-lg border p-4">
                <p className="text-sm text-muted-foreground">Sem Vínculo</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {relacionador.produzidoSemVinculo}
                </p>
              </div>
            </div>

            <div className="rounded-lg border p-4 bg-muted/50">
              <h4 className="font-semibold text-sm mb-2">Período</h4>
              <p className="text-sm">
                {MESES[periodo.mes - 1]} de {periodo.ano}
              </p>
            </div>
          </TabsContent>

          {/* Aba Vínculos */}
          <TabsContent value="vinculos" className="space-y-4">
            {vinculadosExplicitos.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <h4 className="font-semibold text-sm">Vínculos Explícitos ({vinculadosExplicitos.length})</h4>
                </div>
                <div className="space-y-1 text-sm">
                  {vinculadosExplicitos.map(v => (
                    <div key={v.contratoId} className="text-muted-foreground">
                      Contrato {v.contratoId.substring(0, 8)}... — {v.descricao}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vinculadosAgendamento.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-blue-600" />
                  <h4 className="font-semibold text-sm">Vínculos por Agendamento ({vinculadosAgendamento.length})</h4>
                </div>
                <div className="space-y-1 text-sm">
                  {vinculadosAgendamento.map(v => (
                    <div key={v.contratoId} className="text-muted-foreground">
                      Contrato {v.contratoId.substring(0, 8)}... — {v.descricao}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vinculadosPipeline.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-purple-600" />
                  <h4 className="font-semibold text-sm">Vínculos por Pipeline ({vinculadosPipeline.length})</h4>
                </div>
                <div className="space-y-1 text-sm">
                  {vinculadosPipeline.map(v => (
                    <div key={v.contratoId} className="text-muted-foreground">
                      Contrato {v.contratoId.substring(0, 8)}... — {v.descricao}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {semVinculo.length > 0 && (
              <div className="rounded-lg border border-yellow-200 bg-yellow-50/50 p-4 space-y-2">
                <div className="flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <h4 className="font-semibold text-sm text-yellow-900">
                    Sem Vínculo ({semVinculo.length})
                  </h4>
                </div>
                <div className="space-y-1 text-sm">
                  {semVinculo.map(v => (
                    <div key={v.contratoId} className="text-yellow-800">
                      Contrato {v.contratoId.substring(0, 8)}... — {v.descricao}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {vinculosRelacionador.length === 0 && (
              <div className="flex items-center justify-center py-8">
                <p className="text-muted-foreground">Nenhum contrato para este relacionador</p>
              </div>
            )}
          </TabsContent>

          {/* Aba Meta & Comissão */}
          <TabsContent value="meta" className="space-y-4">
            <div className="rounded-lg border p-4">
              <h4 className="font-semibold text-sm mb-2">Meta Vigente</h4>
              <p className="text-2xl font-bold">
                {relacionador.metaVigente ? `R$ ${relacionador.metaVigente?.toLocaleString('pt-BR')}` : '—'}
              </p>
              {relacionador.metaVigente && relacionador.percentualMeta && (
                <p className="text-sm text-muted-foreground mt-2">
                  Atingimento: {Math.round(relacionador.percentualMeta)}%
                </p>
              )}
            </div>

            <div className="rounded-lg border p-4">
              <h4 className="font-semibold text-sm mb-2">Comissão Estimada</h4>
              <p className="text-2xl font-bold">
                {relacionador.comissaoEstimada
                  ? `R$ ${relacionador.comissaoEstimada?.toLocaleString('pt-BR')}`
                  : '—'}
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Baseada na produção vinculada e configuração de comissão
              </p>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}
